// Manual test: await signUp('test+1@example.com','Password123!',{first_name:'John'}) then check Supabase Dashboard > Table Editor > profiles row exists + Dashboard > Auth > Users raw_user_meta_data
import { supabase } from "./supabase.client";

export interface SupabaseAuthSession {
	accessToken: string;
	expiresAt: number;
	user: { id: string; email?: string; firstName?: string; lastName?: string };
}

export async function signUp(
	email: string,
	password: string,
	meta: { first_name?: string; last_name?: string },
) {
	const { data, error } = await supabase.auth.signUp({
		email,
		password,
		options: { data: { first_name: meta.first_name, last_name: meta.last_name } },
	});
	if (error) throw new Error(error.message);
	return data; // contains user (with user_metadata) and session if auto-confirm
}

export async function signIn(email: string, password: string) {
	const { data, error } = await supabase.auth.signInWithPassword({ email, password });
	if (error) throw new Error(error.message);
	return data;
}

export async function signOut() {
	const { error } = await supabase.auth.signOut();
	if (error) throw new Error(error.message);
}

export async function getSupabaseSession(): Promise<SupabaseAuthSession | null> {
	const { data } = await supabase.auth.getSession();
	const session = data.session;
	if (!session) return null;
	const user = session.user;
	return {
		accessToken: session.access_token,
		expiresAt: (session.expires_at ?? 0) * 1000,
		user: {
			id: user.id,
			email: user.email ?? undefined,
			firstName: (user.user_metadata as Record<string, unknown>)?.first_name as
				| string
				| undefined,
			lastName: (user.user_metadata as Record<string, unknown>)?.last_name as
				| string
				| undefined,
		},
	};
}

export function onAuthStateChange(cb: (session: SupabaseAuthSession | null) => void) {
	return supabase.auth.onAuthStateChange((_event, session) => {
		if (!session) return cb(null);
		cb({
			accessToken: session.access_token,
			expiresAt: (session.expires_at ?? 0) * 1000,
			user: {
				id: session.user.id,
				email: session.user.email ?? undefined,
				firstName: (session.user.user_metadata as Record<string, unknown>)?.first_name as
					| string
					| undefined,
				lastName: (session.user.user_metadata as Record<string, unknown>)?.last_name as
					| string
					| undefined,
			},
		});
	});
}

// Retrieve raw user_metadata (guide: supabase.auth.getUser then user.user_metadata)
export async function getCurrentUserMetadata(): Promise<Record<string, unknown> | null> {
	const {
		data: { user },
		error,
	} = await supabase.auth.getUser();
	if (error) throw new Error(error.message);
	return (user?.user_metadata as Record<string, unknown>) ?? null;
}
